import { useState, useEffect, Suspense, lazy } from 'react'
import { Menu, X, Github, Linkedin, Mail, ExternalLink, Code, Briefcase, Heart, Home, FileText } from 'lucide-react'
import { motion } from 'framer-motion'
import { Link, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { fetchResume, type ResumeData } from './lib/api'
import { LoadingSpinner, PDFViewer, ThemeToggle, TinKernLogo } from '@tinkern/ui'
import { MANIFEST_ENTRIES } from '@tinkern/layout'
import ContactModal from './components/ContactModal'

function useCurrentTheme(): 'dark' | 'bright' {
  const [theme, setTheme] = useState<'dark' | 'bright'>(() =>
    document.documentElement.getAttribute('data-theme') === 'bright' ? 'bright' : 'dark'
  )
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setTheme(document.documentElement.getAttribute('data-theme') === 'bright' ? 'bright' : 'dark')
    })
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => obs.disconnect()
  }, [])
  return theme
}

function AnimatedBg() {
  return (
    <div className="animated-bg" aria-hidden="true">
      <div className="starfield starfield-far" />
      <div className="starfield starfield-near" />
    </div>
  )
}

const ECOSYSTEM_SURFACES = new Set(['hub', 'blog', 'after-hours'])

const ProjectsPage = lazy(() => import('./pages/Projects'))
const AboutPage = lazy(() => import('./pages/About'))

const prefetchProjects = () => import('./pages/Projects')
const prefetchAbout = () => import('./pages/About')

const SIDEBAR_COLLAPSED_WIDTH = 'w-20'
const SIDEBAR_EXPANDED_WIDTH = 'w-56'

function Sidebar({ current, onNavigate, isMobile = false }: { current: string, onNavigate?: () => void, isMobile?: boolean }) {
  const [expanded, setExpanded] = useState(false)

  const navigation: Array<{
    name: string
    id: string
    icon: typeof Home
    href?: string
  }> = [
    { name: 'Home', id: 'home', icon: Home },
    { name: 'About', id: 'about', icon: Briefcase },
    { name: 'Projects', id: 'projects', icon: Code },
    { name: 'Resume', id: 'resume', icon: FileText },
    { name: 'After Hours', id: 'life', icon: Heart, href: 'https://life.tinkernlabs.com' },
  ]

  const handleLinkClick = () => {
    if (onNavigate) onNavigate()
  }

  const themeToggle = (
    <ThemeToggle
      className="p-1.5 rounded-md text-gray-500 hover:text-violet border border-violet/20 hover:border-violet/40 transition-colors"
      onThemeChange={(t) => {
        if (typeof window !== 'undefined' && (window as any).posthog) {
          (window as any).posthog.capture('theme_toggled', { theme: t, surface: 'personal' })
        }
      }}
    />
  )

  // Mobile sidebar — always fully expanded, no collapse
  if (isMobile) {
    return (
      <div className="bg-profound-blue/90 backdrop-blur-sm flex flex-col h-full w-full overflow-x-hidden" role="navigation" aria-label="Main">
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto overflow-x-hidden">
          {navigation.map((item) => {
            const Icon = item.icon
            const internalHref = item.id === 'home' ? '/' : `/${item.id}`
            const onEnter = () => { if (item.id === 'about') prefetchAbout() }
            const className = `w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 min-h-[44px] ${
              current === item.id
                ? 'bg-gradient-to-r from-violet to-magenta text-white shadow-[0_0_20px_rgba(127,0,255,0.5)]'
                : 'text-gray-300 hover:bg-violet/20 hover:text-violet active:bg-violet/30 hover:shadow-[0_0_10px_rgba(127,0,255,0.3)]'
            }`
            if (item.href) {
              return (
                <a key={item.id} href={item.href} target="_blank" rel="noopener noreferrer" onClick={handleLinkClick} className={className}>
                  <Icon size={20} />
                  <span className="font-medium">{item.name}</span>
                  <ExternalLink size={14} className="ml-auto opacity-70" />
                </a>
              )
            }
            return (
              <Link key={item.id} to={internalHref} onMouseEnter={onEnter} onClick={handleLinkClick} aria-current={current === item.id ? 'page' : undefined} className={className}>
                <Icon size={20} />
                <span className="font-medium">{item.name}</span>
              </Link>
            )
          })}
        </nav>
        <div className="p-4 border-t border-violet/30">
          <div className="flex justify-center mb-3">{themeToggle}</div>
          <div className="mb-3 text-center">
            <p className="text-[10px] uppercase tracking-[0.15em] text-violet/40 font-medium mb-1.5">TinKern Labs</p>
            <div className="flex justify-center gap-4">
              {MANIFEST_ENTRIES.filter(e => ECOSYSTEM_SURFACES.has(e.id)).map(({ id, label, href }) => (
                <a key={id} href={href} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gray-500 hover:text-violet transition-colors">{label}</a>
              ))}
            </div>
          </div>
          <div className="flex justify-center gap-3" aria-label="Social links">
            <a href="https://github.com/keerthanvenkata" target="_blank" rel="noopener noreferrer" className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-violet active:bg-violet/20 transition-colors" aria-label="GitHub profile"><Github size={24} /></a>
            <a href="https://www.linkedin.com/in/venkata-keerthan/" target="_blank" rel="noopener noreferrer" className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-violet active:bg-violet/20 transition-colors" aria-label="LinkedIn profile"><Linkedin size={24} /></a>
            <a href="mailto:keerthanvenkata@gmail.com" className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-electric-pink active:bg-violet/20 transition-colors" aria-label="Send email"><Mail size={24} /></a>
          </div>
        </div>
      </div>
    )
  }

  // Desktop sidebar — collapsed icon rail by default, expands on hover/focus
  return (
    <div
      data-sidebar
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setExpanded(false)
      }}
      className={`hidden lg:flex fixed left-0 top-0 h-screen bg-profound-blue/70 backdrop-blur-sm border-r border-violet/30 flex-col overflow-hidden transition-[width] duration-200 ease-out ${
        expanded ? `${SIDEBAR_EXPANDED_WIDTH} shadow-[8px_0_30px_rgba(0,0,0,0.35)]` : SIDEBAR_COLLAPSED_WIDTH
      }`}
      style={{ zIndex: 45 }}
      role="navigation"
      aria-label="Main"
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 py-5 border-b border-violet/30 flex-shrink-0 ${expanded ? 'px-5' : 'justify-center px-2'}`} style={{ minHeight: '80px' }}>
        <VKLogo size="md" />
        {expanded && (
          <div className="flex flex-col min-w-0 overflow-hidden whitespace-nowrap">
            <span className="font-body font-semibold tracking-wide text-[15px] text-white leading-tight">Keerthan</span>
            <span className="font-body font-semibold tracking-wide text-[15px] text-violet leading-tight">Venkata</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 space-y-0.5 overflow-hidden">
        {navigation.map((item, i) => {
          const Icon = item.icon
          const internalHref = item.id === 'home' ? '/' : `/${item.id}`
          const isActive = current === item.id
          const onEnter = () => { if (item.id === 'about') prefetchAbout() }
          const cls = `flex items-center mx-2 py-3 border-l-2 rounded-r-lg transition-all duration-200 whitespace-nowrap ${
            expanded ? 'px-3 gap-3' : 'justify-center px-0'
          } ${
            isActive
              ? 'border-violet bg-violet/10 text-violet shadow-[0_0_12px_rgba(127,0,255,0.2)]'
              : 'border-transparent text-gray-300 hover:border-violet/40 hover:bg-violet/10 hover:text-violet'
          }`
          const motionProps = {
            initial: { opacity: 0, x: -8 },
            animate: { opacity: 1, x: 0 },
            transition: { duration: 0.35, delay: 0.05 * i },
          }
          if (item.href) {
            return (
              <motion.a key={item.id} {...motionProps} href={item.href} target="_blank" rel="noopener noreferrer" className={cls} aria-label={item.name} title={expanded ? undefined : item.name}>
                <Icon size={20} className="flex-shrink-0" />
                {expanded && <span className="font-medium">{item.name}</span>}
                {expanded && <ExternalLink size={14} className="ml-auto opacity-70 flex-shrink-0" />}
              </motion.a>
            )
          }
          return (
            <motion.div key={item.id} {...motionProps}>
              <Link to={internalHref} onMouseEnter={onEnter} className={cls} aria-current={isActive ? 'page' : undefined} aria-label={item.name} title={expanded ? undefined : item.name}>
                <Icon size={20} className="flex-shrink-0" />
                {expanded && <span className="font-medium">{item.name}</span>}
              </Link>
            </motion.div>
          )
        })}
      </nav>

      {/* Footer — theme toggle, then ecosystem links, then socials (last) */}
      <div className={`border-t border-violet/30 flex-shrink-0 py-4 ${expanded ? 'px-4' : 'px-2'}`}>
        <div className="flex justify-center mb-3">{themeToggle}</div>

        {expanded ? (
          <div className="mb-3 text-center">
            <p className="text-[10px] uppercase tracking-[0.15em] text-violet/40 font-medium mb-1.5">TinKern Labs</p>
            <div className="flex justify-center gap-3 flex-wrap">
              {MANIFEST_ENTRIES.filter(e => ECOSYSTEM_SURFACES.has(e.id)).map(({ id, label, href }) => (
                <a key={id} href={href} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gray-400 hover:text-violet transition-colors">{label}</a>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex justify-center mb-3">
            <a href="https://labs.tinkernlabs.com" target="_blank" rel="noopener noreferrer" aria-label="TinKern Labs ecosystem" title="TinKern Labs">
              <TinKernLogo size="sm" variant="mono" />
            </a>
          </div>
        )}

        <div className={`flex justify-center gap-3 ${expanded ? '' : 'flex-col items-center'}`} aria-label="Social links">
          <a href="https://github.com/keerthanvenkata" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-violet transition-colors hover:scale-110 transform duration-300" aria-label="GitHub profile" title="GitHub"><Github size={20} /></a>
          <a href="https://www.linkedin.com/in/venkata-keerthan/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-violet transition-colors hover:scale-110 transform duration-300" aria-label="LinkedIn profile" title="LinkedIn"><Linkedin size={20} /></a>
          <a href="mailto:keerthanvenkata@gmail.com" className="text-gray-400 hover:text-electric-pink transition-colors hover:scale-110 transform duration-300" aria-label="Send email" title="Email"><Mail size={20} /></a>
        </div>
      </div>
    </div>
  )
}

type TileAccent = { topColor: string; bgColor: string; labelColor: string; dimColor: string }

const TILE_ACCENTS_DARK: Record<string, TileAccent> = {
  violet:  { topColor: 'rgba(127,0,255,0.85)',   bgColor: 'rgba(127,0,255,0.20)',   labelColor: '#d4baff',                  dimColor: 'rgba(180,140,255,0.65)'  },
  magenta: { topColor: 'rgba(255,0,255,0.75)',   bgColor: 'rgba(255,0,255,0.18)',   labelColor: '#ffc8ff',                  dimColor: 'rgba(255,160,255,0.65)'  },
  pink:    { topColor: 'rgba(255,0,128,0.80)',   bgColor: 'rgba(255,0,128,0.18)',   labelColor: '#ffb8d6',                  dimColor: 'rgba(255,140,180,0.65)'  },
  green:   { topColor: 'rgba(57,255,20,0.75)',   bgColor: 'rgba(57,255,20,0.14)',   labelColor: '#b8ffb0',                  dimColor: 'rgba(120,255,80,0.60)'   },
  blog:    { topColor: 'rgba(127,0,255,0.70)',   bgColor: 'rgba(30,0,126,0.28)',    labelColor: '#ccc0f0',                  dimColor: 'rgba(160,140,220,0.65)'  },
  life:    { topColor: 'rgba(251,191,36,0.85)',  bgColor: 'rgba(100,45,0,0.26)',    labelColor: 'rgba(253,215,90,0.95)',    dimColor: 'rgba(251,191,36,0.65)'   },
}

// Muted hue-family accents for parchment — each tile keeps its color identity but desaturated
const TILE_ACCENTS_BRIGHT: Record<string, TileAccent> = {
  violet:  { topColor: '#8B75B5',  bgColor: 'rgba(220,215,240,0.5)',  labelColor: '#2C2416', dimColor: '#6A5898' },
  magenta: { topColor: '#B56080',  bgColor: 'rgba(240,218,225,0.5)',  labelColor: '#2C2416', dimColor: '#8A4560' },
  pink:    { topColor: '#B5806A',  bgColor: 'rgba(240,225,218,0.5)',  labelColor: '#2C2416', dimColor: '#8A6050' },
  green:   { topColor: '#6A9560',  bgColor: 'rgba(218,232,215,0.5)',  labelColor: '#2C2416', dimColor: '#4A7240' },
  blog:    { topColor: '#6A6AA8',  bgColor: 'rgba(220,220,240,0.5)',  labelColor: '#2C2416', dimColor: '#505090' },
  life:    { topColor: '#C09520',  bgColor: 'rgba(248,240,215,0.55)', labelColor: '#2C2416', dimColor: '#987510' },
}

function HomePage() {
  const theme = useCurrentTheme()
  const PORTRAIT_IMAGE = '13.png'
  const TILE_ACCENTS = theme === 'bright' ? TILE_ACCENTS_BRIGHT : TILE_ACCENTS_DARK
  const [contactOpen, setContactOpen] = useState(false)

  const destinations: Array<{
    label: string
    sub: string
    to?: string
    href?: string
    external?: boolean
    accent: string
  }> = [
    { label: 'Projects',     to: '/projects',     sub: 'AI · platforms · distributed systems', accent: 'violet'  },
    { label: 'About',        to: '/about',        sub: 'how I got here',               accent: 'pink'    },
    { label: 'Resume',       to: '/resume',       sub: 'if you\'re building something cool', accent: 'green'   },
    { label: 'Blog',         href: 'https://blog.tinkernlabs.com',     sub: 'posts on what I\'m thinking about',   external: true, accent: 'blog' },
    { label: 'After Hours',  href: 'https://life.tinkernlabs.com',     sub: 'life outside the lab',       external: true, accent: 'life' },
  ]

  return (
    <div className="min-h-screen relative">
      <AnimatedBg />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20 relative z-10 w-full overflow-x-hidden">

        {/* Hero */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:gap-12 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex-1 text-center lg:text-left space-y-6"
          >
            <motion.h1
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-bold bg-gradient-to-r from-violet via-magenta to-electric-pink bg-clip-text text-transparent text-glow-purple"
            >
              Venkata Keerthan Nimmala
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl sm:text-2xl md:text-4xl font-heading font-semibold text-violet"
            >
              SDE, Applied AI · SurgeVector · Builder, TinKern Labs
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex justify-center lg:justify-start"
            >
              <a
                href="https://life.tinkernlabs.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group/life inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-light text-gray-400 bg-black/30 backdrop-blur-sm border border-violet/20 hover:border-amber-400/40 hover:text-amber-200/90 hover:shadow-[0_0_20px_rgba(251,191,36,0.15)] transition-all duration-300 cursor-pointer"
                style={{ letterSpacing: '0.02em' }}
              >
                <span className="border-b border-transparent group-hover/life:border-amber-400/50 group-hover/life:transition-all group-hover/life:duration-300">
                  elsewhere →
                </span>
              </a>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-lg sm:text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto lg:mx-0 leading-relaxed px-2 sm:px-0"
            >
              Building ML pipelines and production AI systems at SurgeVector (formerly Adaequare).
              <span className="block mt-2 text-electric-pink text-glow-pink">
                Tinkering at{' '}
                <a href="https://labs.tinkernlabs.com" target="_blank" rel="noopener noreferrer" className="underline decoration-electric-pink/70 hover:text-magenta transition-colors">
                  TinKern Labs
                </a>.
              </span>
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-wrap justify-center lg:justify-start gap-4 mt-8"
            >
              <Link to="/projects" className="px-8 py-4 bg-gradient-to-r from-violet to-magenta rounded-lg font-heading font-semibold text-white hover:shadow-[0_0_30px_rgba(127,0,255,0.5)] transition-all duration-300 transform hover:scale-105">
                View Projects
              </Link>
              <button
                type="button"
                onClick={() => setContactOpen(true)}
                className="px-8 py-4 border-2 border-electric-pink rounded-lg font-heading font-semibold text-electric-pink hover:bg-electric-pink hover:text-black transition-all duration-300 transform hover:scale-105 hover:shadow-[0_0_30px_rgba(255,0,128,0.5)]"
              >
                Get In Touch
              </button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:flex flex-shrink-0 items-center justify-center w-full lg:w-auto lg:max-w-[450px]"
          >
            <div className="relative w-full flex items-center justify-center">
              <img
                src={`/media/portrait/${PORTRAIT_IMAGE}`}
                alt="Venkata Keerthan Nimmala"
                className="w-auto max-w-full h-auto max-h-[650px] object-contain"
                style={{
                  filter: theme === 'bright'
                    ? [
                        'drop-shadow(0 0 20px rgba(212,165,116,0.3))',
                        'drop-shadow(0 0 45px rgba(184,134,11,0.2))',
                        'drop-shadow(0 0 70px rgba(139,115,85,0.15))',
                      ].join(' ')
                    : [
                        'drop-shadow(0 0 20px rgba(127, 0, 255, 0.45))',
                        'drop-shadow(0 0 40px rgba(255, 0, 128, 0.4))',
                        'drop-shadow(0 0 60px rgba(255, 90, 0, 0.35))',
                        'drop-shadow(0 0 110px rgba(255, 120, 0, 0.15))',
                      ].join(' '),
                }}
              />
            </div>
          </motion.div>
        </div>

        {/* Section map */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.0 }}
        >
          {/* 6-tile grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            {destinations.map((dest) => {
              const ac = TILE_ACCENTS[dest.accent]
              const tile = (
                <div
                  className="flex flex-col p-5 sm:p-6 rounded-xl border border-white/10 transition-all duration-300 min-h-[160px] sm:min-h-[180px] h-full hover:border-white/25 hover:-translate-y-1.5"
                  style={{
                    backgroundColor: ac.bgColor,
                    borderTopColor: ac.topColor,
                    borderTopWidth: '2px',
                    boxShadow: theme === 'bright'
                      ? '0 2px 8px rgba(0,0,0,0.07), 0 6px 16px rgba(0,0,0,0.05)'
                      : '0 2px 8px rgba(0,0,0,0.5), 0 8px 20px rgba(0,0,0,0.32)',
                  }}
                >
                  <div className="flex items-start justify-between gap-2 mb-2.5">
                    <span className="font-heading font-bold text-base sm:text-lg leading-tight" style={{ color: ac.labelColor }}>
                      {dest.label}
                    </span>
                    <span className="text-sm leading-none flex-shrink-0 mt-0.5" style={{ color: ac.dimColor }}>
                      {dest.external ? '↗' : '→'}
                    </span>
                  </div>
                  <span className="text-sm leading-snug" style={{ color: ac.dimColor }}>
                    {dest.sub}
                  </span>
                </div>
              )

              if (dest.to) {
                return (
                  <Link
                    key={dest.label}
                    to={dest.to}
                    className="h-full block"
                    onMouseEnter={() => {
                      if (dest.to === '/about') prefetchAbout()
                      if (dest.to === '/projects') prefetchProjects()
                    }}
                  >
                    {tile}
                  </Link>
                )
              }
              return (
                <a key={dest.label} href={dest.href} target="_blank" rel="noopener noreferrer" className="h-full block">
                  {tile}
                </a>
              )
            })}
          </div>

          {/* Hub + Contact — combined bottom row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Hub */}
            <a
              href="https://labs.tinkernlabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between px-6 py-7 rounded-xl neon-border transition-all duration-200 hover:-translate-y-0.5 h-full min-h-[120px] sm:min-h-[140px]"
            >
              <div className="flex items-center gap-3">
                <TinKernLogo size="sm" variant="mono" />
                <div>
                  <div className="font-heading font-bold text-base text-white group-hover:text-violet transition-colors duration-200">TinKern Labs</div>
                  <div className="text-xs mt-0.5" style={{ color: theme === 'bright' ? 'rgba(92,78,62,0.75)' : 'rgba(160,140,220,0.6)' }}>studio · products · services</div>
                </div>
              </div>
              <span className="text-sm transition-all duration-200 group-hover:translate-x-0.5" style={{ color: theme === 'bright' ? 'rgba(139,115,85,0.6)' : 'rgba(180,140,255,0.5)' }}>↗</span>
            </a>

            {/* Contact */}
            <div className="flex flex-col justify-between px-6 py-7 rounded-xl border border-white/10 bg-black/20 h-full min-h-[120px] sm:min-h-[140px]">
              <div>
                <p className="text-gray-200 font-medium">Working on something?</p>
                <p className="text-gray-500 text-sm mt-1">Open to freelance work and collaborations.</p>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setContactOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-violet to-magenta text-white hover:shadow-[0_0_20px_rgba(127,0,255,0.4)] transition-all duration-200 text-sm font-medium"
                >
                  <Mail size={14} /> Contact me
                </button>
                <a
                  href="mailto:keerthanvenkata@gmail.com"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-violet/20 to-magenta/20 border border-violet/30 text-gray-200 hover:text-white hover:border-violet/55 hover:from-violet/30 hover:to-magenta/30 transition-all duration-200 text-sm font-medium"
                >
                  <Mail size={14} /> Email
                </a>
                <a
                  href="https://www.linkedin.com/in/venkata-keerthan/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/25 transition-all duration-200 text-sm"
                >
                  <Linkedin size={14} /> LinkedIn
                </a>
              </div>
            </div>

          </div>
        </motion.div>

        {/* Bottom spacer */}
        <div className="mb-16" />

      </div>

      <ContactModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  )
}

function ResumePage() {
  const [resumeData, setResumeData] = useState<ResumeData | null>(null)

  useEffect(() => {
    fetchResume()
      .then(setResumeData)
      .catch(() => setResumeData(null))
  }, [])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return dateString
    }
  }

  const lastUpdated = resumeData?.metadata?.last_updated
    ? formatDate(resumeData.metadata.last_updated)
    : 'Unknown'

  const cacheBuster = resumeData?.current_version || resumeData?.metadata?.last_updated?.replace(/-/g, '') || 'latest'
  const resumePath = `/resume/resume-latest.pdf?v=${cacheBuster}`

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="glass rounded-xl p-6 neon-border">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <h2 className="text-3xl font-bold text-white">Resume</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={resumePath}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-violet to-magenta hover:from-electric-pink hover:to-magenta text-white px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(127,0,255,0.5)] transform hover:scale-105"
            >
              <ExternalLink size={16} />
              View PDF
            </a>
            <a
              href={resumePath}
              download
              className="inline-flex items-center gap-2 bg-gradient-to-r from-violet to-magenta hover:from-electric-pink hover:to-magenta text-white px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(127,0,255,0.5)] transform hover:scale-105"
            >
              <ExternalLink size={16} />
              Download PDF
            </a>
          </div>
        </div>

        <div className="mb-4">
          <PDFViewer src={resumePath} title="Resume Preview" height="800px" />
        </div>

        <div className="text-sm text-gray-400 space-y-2">
          <p>Last updated: {lastUpdated}</p>
          <div className="flex flex-wrap gap-4">
            <p>Having trouble viewing?
              <a href={resumePath} target="_blank" rel="noopener noreferrer" className="text-electric-pink hover:text-magenta ml-1 underline">
                Open in new tab
              </a>
            </p>
            <p>•</p>
            <a href={resumePath} download className="text-electric-pink hover:text-magenta underline">
              Download directly
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

function LifePage() {
  const target = 'https://life.tinkernlabs.com'
  useEffect(() => {
    window.location.replace(target)
  }, [])
  return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-center">
      <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-violet via-magenta to-electric-pink bg-clip-text text-transparent mb-3">
        After Hours moved
      </h1>
      <p className="text-gray-400 mb-6">
        The human side now lives at its own surface — warmer light, softer fonts.
      </p>
      <a href={target} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-amber-300/40 text-amber-100 hover:bg-amber-300/10 transition-colors">
        Continue to life.tinkernlabs.com <ExternalLink size={16} />
      </a>
    </div>
  )
}

function BlogRootRedirect() {
  useEffect(() => {
    window.location.replace('https://blog.tinkernlabs.com')
  }, [])
  return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-center">
      <p className="text-gray-400">Redirecting to TinKern Blog…</p>
    </div>
  )
}

function BlogRedirect() {
  const { id } = useParams<{ id: string }>()
  useEffect(() => {
    if (id) window.location.replace(`https://blog.tinkernlabs.com/${id}`)
  }, [id])
  return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-center">
      <p className="text-gray-400">Redirecting to TinKern Blog…</p>
    </div>
  )
}

function Header({ title }: { title: string }) {
  return (
    <div className="hidden lg:flex bg-black/30 backdrop-blur-sm sticky top-0 z-40 px-6 py-4 items-center transition-all duration-300">
      <h1 className="text-2xl font-heading font-bold text-violet text-glow-purple">{title}</h1>
    </div>
  )
}

function NotFoundPage() {
  const location = useLocation()
  return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-center">
      <h1 className="text-2xl font-bold text-white mb-4">Page Not Found</h1>
      <p className="text-gray-400 mb-6">The page you're looking for doesn't exist.</p>
      <p className="text-gray-500 mb-4 text-sm">Current path: {location.pathname}</p>
      <Link to="/" className="inline-flex items-center gap-2 bg-gradient-to-r from-violet to-magenta hover:from-electric-pink hover:to-magenta text-white px-4 py-2 rounded-lg transition-all duration-300">
        Go Home
      </Link>
    </div>
  )
}

import VKLogo from './components/VKLogo'

export default function App() {
  const [current, setCurrent] = useState('home')
  const [mobile, setMobile] = useState(false)
  const location = useLocation()

  useEffect(() => {
    if (mobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [mobile])

  // Close the mobile menu on any route change, including browser back/forward
  // and back-swipe gestures — not just explicit link taps inside the menu.
  // Also reset scroll so a deep page doesn't open mid-viewport from the previous route.
  useEffect(() => {
    setMobile(false)
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    const path = location.pathname
    if (path === '/projects' || path.startsWith('/projects/')) {
      setCurrent('projects')
    } else if (path === '/resume') {
      setCurrent('resume')
    } else if (path === '/about') {
      setCurrent('about')
    } else if (path === '/life') {
      setCurrent('life')
    } else {
      setCurrent('home')
    }
  }, [location.pathname])

  useEffect(() => {
    const titles: Record<string, string> = {
      home: 'Keerthan Venkata · TinKern Labs',
      about: 'About · Keerthan Venkata',
      projects: 'Projects · Keerthan Venkata',
      resume: 'Resume · Keerthan Venkata',
      life: 'Life · Keerthan Venkata',
    }
    const path = location.pathname
    if (path.startsWith('/projects/')) return
    document.title = titles[current] || 'Keerthan Venkata · TinKern Labs'
  }, [current, location.pathname])

  useEffect(() => {
    const ric = (cb: () => void) => (
      (window as any).requestIdleCallback ? (window as any).requestIdleCallback(cb) : setTimeout(cb, 1200)
    )
    ric(() => {
      prefetchAbout()
      prefetchProjects()
    })
  }, [])

  const title = {
    home: 'Home',
    about: 'About Me',
    projects: 'Projects',
    resume: 'Resume',
    life: 'Life',
  }[current] ?? 'Home'

  return (
    <div className="min-h-screen bg-black text-white flex relative overflow-x-hidden">
      <AnimatedBg />

      {/* Desktop Sidebar — slim always-visible */}
      <Sidebar current={current} />

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-violet/30 px-4 py-3 flex justify-between items-center">
        <div className="relative z-10">
          <VKLogo size="md" />
        </div>
        <ThemeToggle
          className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-violet active:bg-violet/20 transition-colors"
          onThemeChange={(t) => {
            if (typeof window !== 'undefined' && (window as any).posthog) {
              (window as any).posthog.capture('theme_toggled', { theme: t, surface: 'personal' })
            }
          }}
        />
        <button
          onClick={() => setMobile(!mobile)}
          aria-label={mobile ? 'Close menu' : 'Open menu'}
          className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-violet/20 active:bg-violet/30 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-electric-pink relative z-10"
        >
          {mobile ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobile && (
        <div className="lg:hidden fixed inset-0 z-50 bg-profound-blue/90 backdrop-blur-sm flex flex-col">
          <div className="p-4 border-b border-violet/30 flex justify-end items-center flex-shrink-0">
            <button
              onClick={() => setMobile(false)}
              aria-label="Close menu"
              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-violet/20 active:bg-violet/30 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-electric-pink relative z-10"
            >
              <X size={24} />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <Sidebar current={current} onNavigate={() => setMobile(false)} isMobile={true} />
          </div>
        </div>
      )}

      {/* Main Content — shifted right by sidebar width on desktop */}
      <div className="flex-1 flex flex-col min-h-screen lg:min-h-0 w-full overflow-x-hidden lg:pl-20">
        <Header title={title} />
        <main id="main-content" role="main" className="flex-1 overflow-y-auto overflow-x-hidden pt-16 lg:pt-0 w-full">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/projects/:id" element={<ProjectsPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/blog/:id" element={<BlogRedirect />} />
              <Route path="/blog" element={<BlogRootRedirect />} />
              <Route path="/resume" element={<ResumePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/life" element={<LifePage />} />
              <Route path="/outside" element={<Navigate to="/life" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>
        <footer className="bg-black/30 backdrop-blur-sm py-6 px-6 relative z-10 transition-all duration-300">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-gray-400 text-sm">© 2026 Venkata Keerthan Nimmala | Built at TinKern Labs.</p>
            <p className="text-gray-500 text-xs mt-1">Systems that think. Tools that scale.</p>
          </div>
        </footer>
      </div>
    </div>
  )
}
